import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** Move the same map into a native modal top layer, preserving its camera and WebGL context.
 * Works inside WKWebView without depending on the browser Fullscreen API.
 */
export function MapFrame({ expanded, onClose, children }: { expanded: boolean; onClose(): void; children: ReactNode }) {
  const slot = useRef<HTMLDivElement>(null);
  const wasExpanded = useRef(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const [host] = useState(() => {
    const el = document.createElement('div');
    el.className = 'map-frame__host';
    return el;
  });
  useLayoutEffect(() => {
    const modal = dialog.current!;
    if (!expanded) {
      if (host.parentElement !== slot.current) slot.current!.append(host);
      if (wasExpanded.current) host.querySelector<HTMLButtonElement>('[aria-label="Full-screen map"]')?.focus();
      wasExpanded.current = false;
      return;
    }
    wasExpanded.current = true;
    const overflow = document.body.style.overflow;
    modal.append(host);
    modal.showModal();
    document.body.style.overflow = 'hidden';
    host.querySelector<HTMLButtonElement>('[aria-label="Exit full-screen map"]')?.focus();
    return () => {
      modal.close();
      slot.current?.append(host);
      document.body.style.overflow = overflow;
    };
  }, [expanded, host]);
  return <>
    <div ref={slot} className="map-frame" />
    <dialog ref={dialog} className="map-frame__dialog" aria-label="Full-screen map" onCancel={(e) => { e.preventDefault(); onClose(); }}>
    </dialog>
    {createPortal(children, host)}
  </>;
}
